import { eff } from "@/core/utils/effect-ts-abbr";
import { VehicleDetailsComeFrom } from "@/pages/auth/vehicle-details/vehicle-details";
import { routerModel } from "@/router/router.model";
import {
    GetPublicVehiclesQueryParams,
    GetAuthenticatedVehiclesQueryParams,
    PromotionDto,
    APIResponse,
    AuthenticatedVehicleTileDto,
} from "@/shared/api-contract/api-contract";
import { useI18n } from "@/shared/composables/use-i18n";
import { comparisonModel } from "@/shared/models/comparison";
import { watch, watchEffect } from "vue";
import { setup } from "vue-class-component";
import { Emit, Prop, Vue } from "vue-property-decorator";
import { LocationQueryRaw } from "vue-router";
import { appCompanyStatusDialogModel } from "../app-company-status-dialog/app-company-status-dialog.model";
import "./app-vehicle-card.scss";
import { appLanguageSwitcherModel } from "@/shared/components/app-language-switcher/app-language-switcher.model";
import { appAxios } from "@/core/utils/appAxios";
import { sessionModel } from "@/shared/state/session.model";
import { currentUserModel } from "@/shared/state/current-user.model";

export class AppVehicleCardComponent extends Vue {
    @Prop()
    source?: "our-cars" | "my-favorites" | "car-group";

    @Prop({ required: true })
    filters!: GetAuthenticatedVehiclesQueryParams;

    i18n = setup(useI18n);

    @Prop({ required: true })
    vehicleTileDto!: AuthenticatedVehicleTileDto;
    promotionDtos: PromotionDto[] = [];

    isVehicleGroupListOpen = false;

    arrowHover = false;
    isLoadingData = false;

    selectedColors: Readonly<Record<string, readonly number[]>> = {};

    comparisonModel = setup(() => comparisonModel);

    get isAuthenticated() {
        return !!sessionModel.sessionDto.value;
    }

    get isApproved() {
        return (
            !!sessionModel.sessionDto.value && currentUserModel.userDto.value?.is_enabled
        );
    }

    get currency() {
        return "EUR";
    }

    get isSelectedForComparison() {
        return (
            this.vehicleTileDto &&
            this.comparisonModel.selectedItems.has(this.vehicleTileDto.id)
        );
    }

    set isSelectedForComparison(value) {
        this.comparisonModel.toggleComparison(
            this.vehicleTileDto.id,
            false, // this.vehicleDto.promotions === VehicleLabel.specialDeal, TODO: implement!
            value,
        );
    }

    get selectedColorIds(): readonly number[] {
        return [];
        /* return eff.fn.pipe(
            this.selectedColors,
            eff.dict.toArray,
            eff.array.map(({ tuple: [, colorIds] }) => colorIds),
            eff.array.flatten,
            eff.array.uniq(eff.equal.string),
        ); */
    }

    get images() {
        return []; // TODO: add to contract and API
    }

    get colorVariants() {
        /** TODO **/
        /** return eff.fn.pipe(
         this.vehicleGroupDto.color_variants || [],
         eff.array.map((colorVariant) => ({
                ...colorVariant,
                isLight: tinycolor(colorVariant.color.hex).isLight(),
            })),
         );**/
        return [];
    }

    get label() {
        /** TODO: implement
         * switch (this.vehicleDto.promotions) {
            case VehicleLabel.newIn:
                return {
                    text: "NEW IN",
                    iconName: "new-in",
                };
            case VehicleLabel.specialDeal:
                return {
                    text: "Deal of the Day",
                    iconName: "discount",
                };
            case VehicleLabel.reducedPrice:
                return {
                    text: "REDUCED PRICE",
                    iconName: "redused-price",
                };
        }
         */

        return undefined;
    }

    iconNameForPromotion(p: PromotionDto): string {
        switch (p.id) {
            case 1:
                return "redused-price";
            case 2:
                return "discount";
            case 3:
                return "new-in";
            default:
                return "";
        }
    }

    mounted() {
        const currentRoutePath = routerModel.router.currentRoute.value.path;
        const query = routerModel.router.currentRoute.value.query;
        console.log("auth grouped");
        if (
            "car_card_opened_group_id" in query &&
            Number(query.car_card_opened_group_id) === this.vehicleTileDto.id
        ) {
            this.toggleCarGroupList(true);
        }

        watchEffect(() => {
            if (currentRoutePath !== routerModel.router.currentRoute.value.path) {
                return;
            }

            const openedGroupId = Number(query.car_card_opened_group_id) || undefined;

            if (
                this.isVehicleGroupListOpen &&
                this.vehicleTileDto &&
                openedGroupId !== this.vehicleTileDto.id
            ) {
                routerModel.router.replace({
                    query: eff.fn.pipe({
                        ...routerModel.router.currentRoute.value.query,
                        car_card_opened_group_id: this.vehicleTileDto.id.toString(),
                    }),
                });
            }

            if (
                !this.isVehicleGroupListOpen &&
                openedGroupId === this.vehicleTileDto.id
            ) {
                routerModel.router.replace({
                    query: eff.fn.pipe(
                        routerModel.router.currentRoute.value.query,
                        eff.dict.filterWithIndex(
                            (key) => key !== "car_card_opened_group_id",
                        ),
                    ),
                });
            }
        });

        watch(
            () => ({
                lang: appLanguageSwitcherModel.lang.value,
                accessToken: sessionModel.sessionDto.value
                    ? sessionModel.sessionDto.value?.token
                    : undefined,
            }),
            async ({ lang, accessToken }) => {
                this.isLoadingData = true;
                await this.fetchPromotions();
                this.isLoadingData = false;
            },
            { immediate: true },
        );
    }

    routeToCarDetails(navigate: () => void, isSpecial = false) {
        if (isSpecial || !appCompanyStatusDialogModel.show()) {
            navigate();
        }
    }

    routeToCarGroup() {
        /** routerModel.router.push({
            path: `/car-group/${this.vehicleTileDto.group_id}`,
            query: this.viewGroupQuery as LocationQueryRaw,
        }); **/
    }

    toggleCarGroupList(value = !this.isVehicleGroupListOpen): void {
        this.isVehicleGroupListOpen = value;
    }

    @Emit("update-favorite")
    toggleFavorite() {
        return !this.vehicleTileDto.is_favorite;
    }

    /* API Calls ---------------------------------------------------------------------------------------------------- */
    async fetchPromotions() {
        const accessToken = sessionModel.sessionDto.value
            ? sessionModel.sessionDto.value?.token
            : undefined;
        const suffix = accessToken ? "vehicles" : "public/vehicles";

        if (!this.vehicleTileDto.id) return;
        await appAxios.value
            .get<APIResponse<PromotionDto[]>>(
                `/client/web/${suffix}/${this.vehicleTileDto.id}/promotions`,
            )
            .then((response) => {
                if (response.data.status.code === 200) {
                    this.promotionDtos = response.data.data;
                }
            })
            .catch(console.log);
    }
}
